import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, EyeOff, GripHorizontal, Lock } from 'lucide-react';
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

export interface SortableColumnItemProps {
  id: string;
  label: string;
  visible: boolean;
  onToggleVisible: (visible: boolean) => void;
  /** When true, renders a non-draggable placeholder (pinned table columns). */
  pinned?: boolean;
}

const PINNED_ARIA = 'Angeheftete Spalte, nicht umsortier- und ausblendbar';

const columnCardClassName =
  'flex h-44 w-[5.5rem] min-w-[5.5rem] shrink-0 flex-col overflow-hidden rounded-md border border-table-border bg-background';

/** Feste Mindesthöhe = ca. zwei Zeilen → Zieh-Griffe liegen auf einer Linie. */
const columnTitleSlotClassName =
  'flex min-h-[3rem] w-full shrink-0 flex-col items-stretch justify-start px-1 pt-2.5';

const columnTitleClassName =
  'w-full line-clamp-2 break-words text-balance text-center text-xs leading-snug text-text-primary [overflow-wrap:anywhere] hyphens-auto [word-break:break-word]';

const VisibilityIcon: React.FC<{ visible: boolean; muted?: boolean }> = ({
  visible,
  muted,
}) =>
  visible ? (
    <Eye
      className={cn(
        'h-5 w-5 shrink-0',
        muted ? 'text-text-muted' : 'text-text-secondary',
      )}
      aria-hidden
    />
  ) : (
    <EyeOff className="h-5 w-5 shrink-0 text-text-muted" aria-hidden />
  );

/** Full-width toggle: Sichtbarkeit per Klick (Eye / EyeOff). */
const ColumnVisibilityButton: React.FC<{
  visible: boolean;
  label: string;
  pinned: boolean;
  onToggle?: (next: boolean) => void;
}> = ({ visible, label, pinned, onToggle }) => (
  <button
    type="button"
    disabled={pinned}
    aria-pressed={pinned ? undefined : visible}
    aria-label={
      pinned
        ? PINNED_ARIA
        : visible
          ? `${label} in der Tabelle ausblenden`
          : `${label} in der Tabelle anzeigen`
    }
    className={cn(
      'flex h-10 w-full shrink-0 items-center justify-center border-t border-table-border bg-muted/40 transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0',
      pinned
        ? 'cursor-not-allowed opacity-80'
        : 'cursor-pointer hover:bg-muted/80',
    )}
    onClick={() => onToggle?.(!visible)}
  >
    <VisibilityIcon visible={visible} muted={pinned} />
  </button>
);

const PinnedColumnCard: React.FC<{ id: string; label: string }> = ({
  id,
  label,
}) => (
  <Tooltip delayDuration={0}>
    <TooltipTrigger asChild>
      <div
        className={cn(columnCardClassName, 'opacity-60')}
        aria-disabled="true"
        data-column-id={id}
      >
        <div className={columnTitleSlotClassName}>
          <span className={columnTitleClassName} lang="de" title={label}>
            {label}
          </span>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center px-2">
          <Lock
            className="size-8 shrink-0 text-text-secondary"
            aria-hidden="true"
          />
        </div>
        <ColumnVisibilityButton visible label={label} pinned />
      </div>
    </TooltipTrigger>
    <TooltipContent
      side="top"
      sideOffset={6}
      lang="de"
      className="z-[200] max-w-[14rem] border border-background/15 px-3 py-2 text-left shadow-md"
    >
      <p className="text-xs font-medium leading-tight">Angeheftete Spalte</p>
      <p className="mt-1 text-xs leading-snug text-background/90">
        Nicht umsortier- und ausblendbar.
      </p>
    </TooltipContent>
  </Tooltip>
);

const DraggableColumnCard: React.FC<{
  id: string;
  label: string;
  visible: boolean;
  onToggleVisible: (visible: boolean) => void;
}> = ({ id, label, visible, onToggleVisible }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      aria-roledescription="Spalte"
      className={cn(columnCardClassName, isDragging && 'opacity-50 shadow-md')}
    >
      <div className={columnTitleSlotClassName}>
        <span className={columnTitleClassName} lang="de" title={label}>
          {label}
        </span>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center px-2">
        <button
          type="button"
          aria-label={`${label} verschieben`}
          className="flex size-16 cursor-grab touch-manipulation items-center justify-center rounded-lg text-text-secondary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripHorizontal
            className="h-9 w-9 shrink-0"
            strokeWidth={2.25}
            aria-hidden
          />
        </button>
      </div>
      <ColumnVisibilityButton
        visible={visible}
        label={label}
        pinned={false}
        onToggle={onToggleVisible}
      />
    </div>
  );
};

export const SortableColumnItem: React.FC<SortableColumnItemProps> = (
  props,
) => {
  if (props.pinned) {
    return <PinnedColumnCard id={props.id} label={props.label} />;
  }
  return (
    <DraggableColumnCard
      id={props.id}
      label={props.label}
      visible={props.visible}
      onToggleVisible={props.onToggleVisible}
    />
  );
};
